import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import { useT } from '../../../../i18n';

/**
 * Ba ô chọn thời gian của nhóm Nhật ký — port `ChonKhoangGio`, `ChonNgay`, `ChonThoiDiem` native. Lịch và nút của
 * `DatePicker` đi theo ngôn ngữ nhờ `ConfigProvider locale` của `providers.tsx` (native truyền `vi_VN` cứng từng ô).
 * Ngày hiện `DD/MM/YYYY` ở cả hai ngôn ngữ (ADR-UI-0010), giờ 24 giờ.
 *
 * Giá trị là CHUỖI giờ ĐỊA PHƯƠNG không múi giờ — đúng hợp đồng của `nghiep-vu/moc-chon.ts`:
 * `YYYY-MM-DDTHH:mm` (có giờ) hoặc `YYYY-MM-DD` (ngày lịch). Rỗng khi chưa chọn. Không đi qua `Date`: một ngày lịch đổi
 * sang mốc rồi đổi lại có thể lệch một ngày ở máy có múi giờ khác.
 */

const GIA_TRI_GIO = 'YYYY-MM-DDTHH:mm';
const GIA_TRI_NGAY = 'YYYY-MM-DD';
const HIEN_GIO = 'DD/MM/YYYY HH:mm';
const HIEN_NGAY = 'DD/MM/YYYY';

/**
 * Chuỗi đã lưu → giá trị của ô. Một NGÀY trần (liên kết cũ, URL trước khi có giờ) hiện thành 00:00 ở mốc dưới và
 * 23:59 ở mốc trên — đúng khoảng mà `moc-chon.ts` gửi đi cho nó.
 */
function doc(chuoi: string | undefined, gioMacDinh: string): Dayjs | null {
  if (!chuoi) return null;
  const d = dayjs(chuoi.includes('T') ? chuoi : `${chuoi}T${gioMacDinh}`);
  return d.isValid() ? d : null;
}

/**
 * Khoảng "Tuỳ chọn" có GIỜ:PHÚT, dùng chung cho Sử dụng AI, Tổng quan, Cảnh báo và Nhật ký thao tác. Giờ mặc định khi
 * mới chọn ngày là 00:00 và 23:59 — tức "cả ngày"; mốc trên 23:59 được tính trọn phút (`mocCuoi`).
 */
export function ChonKhoangGio({
  tu,
  den,
  onDoi,
}: {
  tu?: string;
  den?: string;
  onDoi: (tu: string, den: string) => void;
}) {
  const { t } = useT();
  const dau = doc(tu, '00:00');
  const cuoi = doc(den, '23:59');
  return (
    <DatePicker.RangePicker
      showTime={{ format: 'HH:mm', defaultOpenValue: [dayjs().startOf('day'), dayjs().endOf('day')] }}
      format={HIEN_GIO}
      placeholder={[t('nktv.loc.tuGio'), t('nktv.loc.denGio')]}
      aria-label={t('nktv.loc.moc')}
      value={dau || cuoi ? [dau, cuoi] : null}
      onChange={(ds) => onDoi(ds?.[0]?.format(GIA_TRI_GIO) ?? '', ds?.[1]?.format(GIA_TRI_GIO) ?? '')}
      className="w-[22rem] max-w-full"
    />
  );
}

interface ONgayProps {
  id?: string;
  value: string;
  onChange: (moi: string) => void;
  placeholder?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** Ngày hôm nay theo giờ trên máy người dùng, ở dạng giá trị của `ChonNgay`. */
export function homNay(): string {
  return dayjs().format(GIA_TRI_NGAY);
}

/** Ô chọn NGÀY (không giờ) — giá trị `YYYY-MM-DD`, đúng dạng audit-service nhận và trả (`tinh_tu` của hạn mức). */
export function ChonNgay({
  id,
  value,
  onChange,
  placeholder,
  className,
  choXoa = true,
  'aria-describedby': moTa,
  'aria-invalid': sai,
}: ONgayProps & { choXoa?: boolean }) {
  const ngay = value === '' ? null : dayjs(value);
  return (
    <DatePicker
      id={id}
      aria-describedby={moTa}
      aria-invalid={sai}
      status={sai ? 'error' : undefined}
      format={HIEN_NGAY}
      allowClear={choXoa}
      placeholder={placeholder ?? HIEN_NGAY.toLowerCase()}
      value={ngay?.isValid() ? ngay : null}
      onChange={(d) => onChange(d ? d.format(GIA_TRI_NGAY) : '')}
      className={className ?? 'w-full'}
    />
  );
}

/**
 * Ô chọn NGÀY GIỜ — thay `datetime-local` của trình duyệt (lịch tiếng Anh, giờ AM/PM, mỗi trình duyệt một kiểu). Giữ
 * hợp đồng giá trị của ô cũ: `YYYY-MM-DDTHH:mm` giờ địa phương, rỗng khi chưa chọn.
 */
export function ChonThoiDiem({
  id,
  value,
  onChange,
  placeholder,
  className,
  'aria-describedby': moTa,
  'aria-invalid': sai,
}: ONgayProps) {
  const ngay = value === '' ? null : dayjs(value);
  return (
    <DatePicker
      id={id}
      aria-describedby={moTa}
      aria-invalid={sai}
      status={sai ? 'error' : undefined}
      showTime={{ format: 'HH:mm' }}
      format={HIEN_GIO}
      needConfirm={false}
      placeholder={placeholder ?? HIEN_GIO.toLowerCase()}
      value={ngay?.isValid() ? ngay : null}
      onChange={(d) => onChange(d ? d.format(GIA_TRI_GIO) : '')}
      className={className ?? 'w-full'}
    />
  );
}
